import { useEventCallback } from '@mui/material';
import { Fragment, ReactElement, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { RegisterResearchCrubs } from 'practice/constants/practice-register-crubs';
import FilterResearchRegister from 'practice/components/register/research/filter';
import { Form, Formik } from 'formik';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { validationSchema } from 'practice/constants/research-register-schema';
import TableRegister from 'practice/components/register/research/table-register';
import ListHeader from 'shell/components/shared/list-header';
import { useNavigate } from 'react-router-dom';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PracticeAuthorizations } from 'shell/authorizations/practice';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { HttpResponseList } from 'shell/models/http-response';
import { TypeRegisterData } from 'shell/models/practice-lists';
import { AxiosResponse } from 'axios';
import { BodyResearchRegister, ResearchRegisterVM } from 'practice/view-models/research-register';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformDataset } from './utils';

export const authsObj = Object.freeze({
  read: [PracticeAuthorizations.RicercaPratiche],
  create: [PracticeAuthorizations.NuovoRegistro],
  edit: [PracticeAuthorizations.ModificaRegistro],
  delete: [PracticeAuthorizations.EliminaRegistro],
});

const ResearchRegister = (): ReactElement => {
  const navigate = useNavigate();

  const [registers, getRegisterList] = useHttpFetch<
    HttpResponseList<TypeRegisterData>,
    Array<BodyResearchRegister>
  >(`${__PRACTICE_API_URL__}/pratiche/tipo-registro`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response: AxiosResponse<HttpResponseList<TypeRegisterData>>) => {
      const { data } = response;
      return data?.payload?.list?.map((item) => ({
        type: item.key?.tipoRegistro || '',
        description: item.descrizioneRegistro || '',
      }));
    },
  });

  const onSubmitFilter = useEventCallback((values: ResearchRegisterVM) => {
    const queryString = transformDataset(values);

    getRegisterList({
      params: {},
      paramsSerializer: () => queryString,
    });
  });
  const auths = useActionAuthorization(authsObj);

  const deleteRegister = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/tipo-registro`, {
    options: { method: 'DELETE' },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BodyResearchRegister>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<BodyResearchRegister>>({
    element: null,
    open: false,
  });

  return (
    <Fragment>
      <Breadcrumbs items={RegisterResearchCrubs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo registro"
        addBtnLabel="Aggiungi nuovo registro"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/pratiche/ricerca-registro/nuovo');
        }}
        title="Ricerca Registro"
      />
      <div className="circle" />
      <Formik
        initialValues={{ filter: { typeRegister: '', description: '', orders: {} } }}
        onSubmit={(values) => onSubmitFilter(values)}
        validationSchema={validationSchema}
      >
        <Form>
          <FilterResearchRegister />
          {registers ? (
            <TableRegister
              activeDelete={auths.delete}
              activeEdit={auths.edit}
              getRegisterList={getRegisterList}
              registers={registers.data as Array<BodyResearchRegister>}
              setDeleteAlertModal={setDeleteAlertModal}
            />
          ) : undefined}
        </Form>
      </Formik>
      <DeleteAlertModal
        title="Cancellazione Registro"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteRegister({
              url: `/${deleteAlertModal.element?.type}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Registro"
        message={`La cancellazione della funzione ${deleteSuccessModal.element?.type} è avvenuta con successo.`}
        onClose={() => {
          onSubmitFilter({ filter: { typeRegister: '', description: '', orders: {} } });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          onSubmitFilter({ filter: { typeRegister: '', description: '', orders: {} } });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

ResearchRegister.displayName = 'Research Register Page component';

export default ResearchRegister;
